import { useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  type ComboboxOption,
  ComboboxField,
  DropdownMenuItem,
  DropdownMenuSeparator,
  Form,
  Modal,
  UserMenu as UserMenuShell,
  UserMenuHeader,
} from '@rammetec/design-system'
import { Activity, Building2, LogOut, Settings, User, UserCog, UserMinus, UserRoundCog } from 'lucide-react'
import type { AuthUser } from '@rammetec/data-access'
import { rtLogService } from '@rammetec/core'
import {
  canRoleImpersonate,
  getImpersonatableRoles,
  useCurrentOrganizationData,
  useCurrentOrgUserData,
  useCurrentProfile,
  useCurrentUser,
  useImpersonation,
  useOrganizationUsers,
} from '@syscolect/application'
import { authService } from '@/services/authService'

export interface UserMenuProps {
  user: AuthUser
}

/** Associates the footer submit button (rendered outside `<form>`) with the form. */
const IMPERSONATE_FORM_ID = 'console-impersonate-form'

function getInitials(label: string): string {
  const parts = label.split(/[\s@.]+/).filter(Boolean)
  const first = parts[0]?.[0] ?? '?'
  const second = parts[1]?.[0] ?? ''
  return (first + second).toUpperCase()
}

function getUserPhotoUrl(user: AuthUser): string | undefined {
  const u = user as Record<string, unknown>
  const raw = u.photoURL ?? u.photoUrl
  return typeof raw === 'string' && raw.trim() !== '' ? raw : undefined
}

/**
 * Header avatar + dropdown — composed via the design-system UserMenu shell (0006).
 * Adds user impersonation for system/admin roles: the original (logged-in) user
 * can temporarily assume another org user's view. Permission and orchestration
 * stay in the page; the impersonation state itself lives in `useImpersonation`.
 */
export function UserMenu({ user }: UserMenuProps) {
  const navigate = useNavigate()
  const { currentUserData, isImpersonating } = useCurrentUser()
  const { orgUserData } = useCurrentOrgUserData()
  const { organizationData } = useCurrentOrganizationData()
  const { profileData } = useCurrentProfile()
  const { startImpersonation, stopImpersonation } = useImpersonation()

  const [dialogOpen, setDialogOpen] = useState(false)

  // Impersonation picker as a DS Form. Enter on the (single) field focuses the
  // confirm button (CrudGrid pattern); a second Enter confirms.
  const impersonateForm = useForm<{ userId: string }>({ defaultValues: { userId: '' } })
  const selectedUserId = impersonateForm.watch('userId')
  const confirmButtonRef = useRef<HTMLButtonElement>(null)

  // When NOT impersonating, the current user IS the original (logged-in) user,
  // so the current org membership role is the authoritative permission source.
  const effectiveRole = orgUserData?.orgUserData?.role
  const canImpersonate = !isImpersonating && canRoleImpersonate(effectiveRole)

  // Only fetch org users when the picker is actually open.
  const { users: organizationUsers, isLoading: usersLoading } = useOrganizationUsers(
    canImpersonate && dialogOpen,
  )

  const impersonatableOptions: ComboboxOption[] = useMemo(() => {
    if (!canImpersonate) return []
    const allowed = getImpersonatableRoles(effectiveRole)
    if (allowed.length === 0) return []
    return organizationUsers
      .filter((u) => u.userData.id !== currentUserData?.id)
      .filter((u) => allowed.includes((u.orgUserData.role ?? 'user') as (typeof allowed)[number]))
      .map((u) => {
        const name = u.userData.name?.trim() || u.userData.email || u.userData.id
        const email = u.userData.email && u.userData.name ? ` (${u.userData.email})` : ''
        const role = u.orgUserData.role ? ` — ${u.orgUserData.role}` : ''
        return { value: u.userData.id, label: `${name}${email}${role}` }
      })
  }, [canImpersonate, effectiveRole, organizationUsers, currentUserData?.id])

  const photoUrl = getUserPhotoUrl(user)
  const realLabel = user.email ?? user.userId
  const activeName = currentUserData?.name?.trim() || currentUserData?.email || realLabel
  const activeEmail = currentUserData?.email ?? user.email ?? user.userId

  const handleLogout = async () => {
    await authService.logout()
    navigate('/login', { replace: true })
  }

  const openImpersonationDialog = () => {
    impersonateForm.reset({ userId: '' })
    setDialogOpen(true)
  }

  const onImpersonateSubmit = ({ userId }: { userId: string }) => {
    if (!userId) return
    startImpersonation(userId)
    rtLogService.info('Started impersonating user', { userId })
    setDialogOpen(false)
  }

  const handleStopImpersonation = () => {
    stopImpersonation()
    rtLogService.info('Stopped impersonation')
  }

  return (
    <>
      <UserMenuShell
        src={photoUrl}
        alt={realLabel ? `Foto de ${realLabel}` : 'Foto do usuário'}
        fallback={getInitials(activeName)}
        triggerAriaLabel={isImpersonating ? 'Menu do usuário (personificando)' : 'Abrir menu do usuário'}
        status={isImpersonating ? 'warning' : undefined}
        header={
          <UserMenuHeader
            icon={isImpersonating ? <UserRoundCog /> : <User />}
            primary={activeName}
            secondary={activeEmail}
          />
        }
      >
        {organizationData ? (
          <div className="flex items-center gap-2 px-2 py-1.5 text-xs text-fg-muted">
            <Building2 className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{organizationData.name}</span>
          </div>
        ) : null}
        {profileData ? (
          <div className="flex items-center gap-2 px-2 py-1.5 text-xs text-fg-muted">
            <User className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{profileData.name}</span>
          </div>
        ) : null}

        {isImpersonating ? (
          <>
            <DropdownMenuSeparator />
            <div className="px-2 py-1.5 text-xs text-amber-600 dark:text-amber-500">
              Personificando <span className="font-medium">{activeName}</span>
            </div>
            <DropdownMenuItem onSelect={handleStopImpersonation} className="gap-2 text-destructive">
              <UserMinus className="h-4 w-4" />
              Parar personificação
            </DropdownMenuItem>
          </>
        ) : null}

        {canImpersonate ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={openImpersonationDialog} className="gap-2">
              <UserCog className="h-4 w-4" />
              Personificar usuário
            </DropdownMenuItem>
          </>
        ) : null}

        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate('/settings')} className="gap-2">
          <Settings className="h-4 w-4" />
          Configurações
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => navigate('/status')} className="gap-2">
          <Activity className="h-4 w-4" />
          Status
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={handleLogout} className="gap-2">
          <LogOut className="h-4 w-4" />
          Sair
        </DropdownMenuItem>
      </UserMenuShell>

      {canImpersonate ? (
        <Modal
          name="console.user-menu.impersonate"
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          title="Personificar usuário"
          description="Assuma temporariamente a visão de outro usuário da organização."
          size="sm"
          footer={
            <>
              <Button variant="ghost" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button
                ref={confirmButtonRef}
                type="submit"
                form={IMPERSONATE_FORM_ID}
                disabled={!selectedUserId}
              >
                Personificar
              </Button>
            </>
          }
        >
          {usersLoading ? (
            <p className="text-sm text-fg-muted">Carregando usuários…</p>
          ) : impersonatableOptions.length === 0 ? (
            <p className="text-sm text-fg-muted">Nenhum usuário disponível para personificação.</p>
          ) : (
            <Form
              id={IMPERSONATE_FORM_ID}
              form={impersonateForm}
              onSubmit={onImpersonateSubmit}
              onLastFieldEnter={() => confirmButtonRef.current?.focus()}
            >
              <ComboboxField
                name="userId"
                options={impersonatableOptions}
                placeholder="Buscar usuário…"
                emptyMessage="Nenhum usuário encontrado."
              />
            </Form>
          )}
        </Modal>
      ) : null}
    </>
  )
}

export function UserMenuFallback() {
  return (
    <Button variant="ghost" size="sm" disabled>
      <User className="h-4 w-4" />
      Carregando
    </Button>
  )
}
